import { Component, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { RouterModule } from '@angular/router';
import { UserAdminService } from '../../shared/services/user-admin.service';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { requestGetUserPayload, requestRemoveUserFactorPayload } from '../../shared/models/model';
import { UserInterface } from '../../shared/models/userData';
import config from '../../../assets/config.json';
import { Subscription } from 'rxjs';
import { OktaService } from '../../shared/services/okta.service';
@Component({
  selector: 'app-user-admin',
  standalone: true,
  imports: [RouterModule, FormsModule, CommonModule],
  templateUrl: './user-admin.component.html',
  styleUrl: './user-admin.component.scss',
})
export class UserAdminComponent implements OnDestroy {
  @ViewChild('idpAdmin', { static: false }) container!: ElementRef;
  errorMessage: string = '';
  userId: string = '';
  alertResponse: string = '';
  userDetails: UserInterface = {};
  isSearch: boolean = false;
  removeMfaResponse: string = '';
  mfaerrorMessage: string = '';
  byPassCodeResponse: string = '';
  byPasserrorMessage: string = '';
  resetIDPResponse: string = '';
  resetIDPerrorMessage: string = '';
  userSubscription: Subscription = new Subscription();
  generateBypassCodeSubscription: Subscription = new Subscription();
  clearBypassCodeSubscription: Subscription = new Subscription();
  clearMfaSubscription: Subscription = new Subscription();
  resetIDPSubscription: Subscription = new Subscription();
  siteUrl: string = (() => {
    try {
      return config.siteUrl ? new URL(config.siteUrl).hostname : 'financial-professional.trustage.com';
    } catch {
      return 'financial-professional.trustage.com';
    }
  })();
  constructor(
    private _userAdminService: UserAdminService,
    private _okta: OktaService,
  ) {}

  //Fetching the user data
  getUserData(userId: string, bypasscode: boolean): void {
    this.userDetails = {};
    const payload: requestGetUserPayload = {
      requestingSystem: 'FinPro',
      userName: userId,
      browserName: this._okta.getBrowserName(),
    };
    this.userSubscription = this._userAdminService.getUser(payload).subscribe({
      next: (data: UserInterface) => {
        this.userDetails = data;
        if (this.userDetails.mfaDetails) {
          this.userDetails.mfaDetails.enrolledDate = data.mfaDetails?.enrolledDate
            ? this.dateProcess(data.mfaDetails?.enrolledDate)
            : null;
        }
        this.userDetails.idpEnrolledDate = data.idpEnrolledDate ? this.dateProcess(data.idpEnrolledDate) : null;
        if (data.response === 'User not Found') {
          this.alertResponse = 'User not found';
          this.clearAlertResponse();
          this.errorMessage = `Unable to find ${this.userId}. ${this.userId} must log-in at least once before they can be found in this tool.`;
        } else {
          this.userDetails.response = true;
          this.alertResponse = data.userId?.toUpperCase() + ' loaded';
          this.clearAlertResponse();
          if (bypasscode) {
            this.getBypassCode();
          }
        }
        this.isSearch = true;
      },
      //Error handling
      error: (error: string) => {
        this.isSearch = true;
        this.errorMessage = error;
      },
    });
  }
  dateProcess(date: string): string {
    return new Date(date)
      .toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: true,
      })
      .replace(' at ', ' ');
  }

  searchUser(byPasscode: boolean = false): void {
    this.isSearch = false;
    this.alertResponse = 'Searching...';
    this.errorMessage = '';
    if (this.userId.trim() === '') {
      this.errorMessage = 'The user ID is required.';
      this.alertResponse = 'User not found';
      this.clearAlertResponse();
      this.userDetails = {};
      this.isSearch = true;
    } else {
      this.getUserData(this.userId.trim(), byPasscode);
    }
  }
  removeMfaFactor(factorType: number): void {
    this.mfaerrorMessage = '';
    const payload: requestRemoveUserFactorPayload = {
      requestingSystem: 'FinPro',
      userName: this.userDetails.userId ? this.userDetails.userId : '',
      factorType: factorType,
    };
    this._userAdminService.removeUserFactor(payload).subscribe({
      next: (data: any) => {
        this.removeMfaResponse = data.success
          ? 'An MFA factor has been cleared for ' + this.userDetails.userId?.toUpperCase()
          : '';
        setTimeout(() => {
          this.removeMfaResponse = '';
        }, Number(config.userAdminDetails_actions_loading));
        this.getUserData(this.userId, false);
      },
      //Error handling
      error: (error: string) => {
        this.mfaerrorMessage = error ? 'Error with the server. Please try again later.' : '';
      },
    });
  }
  clearAlertResponse() {
    setTimeout(() => {
      this.alertResponse = '';
    }, Number(config.userAdminDetails_message_loading));
  }
  toggleDiv(index: number) {
    const divs = this.container.nativeElement.querySelectorAll('.toggle-div');
    if (divs[index]) {
      divs[index].hidden = !divs[index].hidden;
    }
  }
  onClickGenerateBypassCode() {
    if (this.userId.trim() === '') {
      this.searchUser(false);
    } else if (this.userDetails.userId) {
      this.getBypassCode();
    } else if (this.userId.trim() !== '') {
      this.searchUser(true);
    }
  }
  onClickClearBypassCode() {
    if (this.userId.trim() === '') {
      this.searchUser(false);
    } else if (this.userDetails.userId) {
      this.clearBypassCode();
    } else if (this.userId.trim() !== '') {
      this.searchUser();
      this.clearBypassCode();
    }
  }
  getBypassCode() {
    const payload = {
      requestingSystem: 'FinPro',
      userName: this.userDetails.userId,
    };
    this.byPassCodeResponse = '';
    this.byPasserrorMessage = '';
    this._userAdminService.generateByPassCode(payload).subscribe({
      next: (data: any) => {
        this.byPassCodeResponse =
          data.response && data.response === 'Bypass code generated successfully.'
            ? 'New bypass code generated for ' + this.userDetails.userId?.toUpperCase()
            : '';
        this.userDetails.bypassCode = data.bypassCode;
        this.userDetails.bypassCodeDate = data.bypassCodeDate;
        setTimeout(() => {
          this.byPassCodeResponse = '';
        }, Number(config.userAdminDetails_actions_loading));
      },
      //Error handling
      error: (error: string) => {
        this.byPasserrorMessage = error ? 'Error with the server. Please try again later.' : '';
      },
    });
  }
  clearBypassCode() {
    const payload = {
      requestingSystem: 'FinPro',
      userName: this.userId,
    };
    this.byPassCodeResponse = '';
    this.byPasserrorMessage = '';
    this._userAdminService.clearByPassCode(payload).subscribe({
      next: (data: any) => {
        this.byPassCodeResponse =
          data.response && data.response === 'Bypass code cleared successfully.'
            ? 'Bypass code cleared for ' + this.userId.toUpperCase()
            : '';
        this.userDetails.bypassCode = data.bypassCode;
        this.userDetails.bypassCodeDate = data.bypassCodeDate;
        setTimeout(() => {
          this.byPassCodeResponse = '';
        }, Number(config.userAdminDetails_actions_loading));
      },
      //Error handling
      error: (error: string) => {
        this.byPasserrorMessage = error ? 'Error with the server. Please try again later.' : '';
      },
    });
  }
  resetIDP() {
    const payload = {
      requestingSystem: 'FinPro',
      userName: this.userId,
    };
    this.resetIDPResponse = '';
    this.resetIDPerrorMessage = '';
    this._userAdminService.resetIDP(payload).subscribe({
      next: (data: any) => {
        this.resetIDPResponse = data && data.success ? 'IDP has been reset for ' + this.userId.toUpperCase() : '';
        this.userDetails.idpMethod = null;
        this.userDetails.idpEnrolledDate = null;
        setTimeout(() => {
          this.resetIDPResponse = '';
        }, Number(config.userAdminDetails_actions_loading));
      },
      //Error handling
      error: (error: string) => {
        this.resetIDPerrorMessage = error ? 'Error with the server. Please try again later.' : '';
      },
    });
  }
  ngOnDestroy(): void {
    this.userSubscription.unsubscribe();
    this.generateBypassCodeSubscription.unsubscribe();
    this.clearBypassCodeSubscription.unsubscribe();
    this.clearMfaSubscription.unsubscribe();
    this.resetIDPSubscription.unsubscribe();
  }
}
